// Caller profile. Read under RLS: a user can only ever resolve a profile inside their own
// organisation, and the identity helpers of the database do the rest.
import type { Profil } from '@horizon/types';
import { getClient } from './client.js';
import { erreurLocale, toErreur, withRetry } from './erreurs.js';

const COLONNES = 'user_id,organisation_id,role,prenom,nom,actif';

/**
 * Every profile of the caller organisation (profils_select policy), in the stable
 * user_id order, which reproduces the seed order of the reference screen. Also used to
 * resolve audit authors and assigned advisors into display names (order indifferent).
 */
export async function listerProfils(): Promise<readonly Profil[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('profils')
      .select(COLONNES)
      .order('user_id', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Profil[];
  });
}

/**
 * Profile attached to an authenticated user. Throws ACCES_REFUSE when no active profile
 * exists, because an authenticated account without a profile has no perimeter at all.
 */
export async function chargerProfil(userId: string): Promise<Profil> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('profils')
      .select(COLONNES)
      .eq('user_id', userId)
      .maybeSingle();
    if (error !== null) {
      throw toErreur(error);
    }
    if (data === null) {
      throw erreurLocale('ACCES_REFUSE', 'no profile attached to this account');
    }
    if (data.actif !== true) {
      throw erreurLocale('ACCES_REFUSE', 'account deactivated');
    }
    return data as unknown as Profil;
  });
}

/**
 * Update the identity fields of a profile. The write policy restricts the mutation to
 * the administrateur role; a silent zero-row update is surfaced as ACCES_REFUSE so the
 * caller never believes a rejected write succeeded.
 */
export async function mettreAJourProfil(
  userId: string,
  modifications: { readonly prenom: string; readonly nom: string },
): Promise<Profil> {
  const { data, error } = await getClient()
    .from('profils')
    .update({ prenom: modifications.prenom, nom: modifications.nom })
    .eq('user_id', userId)
    .select(COLONNES);
  if (error !== null) {
    throw toErreur(error);
  }
  const ligne = (data ?? [])[0];
  if (ligne === undefined) {
    throw erreurLocale('ACCES_REFUSE', 'profile update rejected by RLS');
  }
  return ligne as unknown as Profil;
}
